/**
 * Flask damage shard burst — outward scatter from flask on boulder hit.
 * Ballistic gravity, no fade; culled when off-screen (not timed).
 * Light HP hits use a small count; flask break uses the full burst.
 */

import { getFallGravityForRunHeight } from "../config/constants.js";

/** Shard count on non-lethal boulder damage (HP remaining). */
export var FLASK_SHARD_BURST_DAMAGE_COUNT_MIN = 1;
export var FLASK_SHARD_BURST_DAMAGE_COUNT_MAX = 3;
/** Shard count when the flask breaks (HP depleted). */
export var FLASK_SHARD_BURST_COUNT_MIN = 12;
export var FLASK_SHARD_BURST_COUNT_MAX = 16;
export var FLASK_SHARD_BURST_SIZE_MIN_RATIO = 0.2125;
export var FLASK_SHARD_BURST_SIZE_MAX_RATIO = 0.35;
export var FLASK_SHARD_BURST_OPACITY = 0.92;
/** Multiplier on outward shard velocity (Fall burst VFX). */
export var FLASK_SHARD_BURST_SPEED_SCALE = 10.125;
/**
 * Burst arc in math degrees from +X (right): 0° = right, 90° = up, 180° = left.
 * Canvas vy uses −sin so the upper half-circle flies sideways/up, not into the sea.
 */
export var FLASK_SHARD_BURST_ANGLE_MIN_RAD = 0;
export var FLASK_SHARD_BURST_ANGLE_MAX_RAD = Math.PI;
/**
 * Matter.js default gravity.scale is 0.001, so gravity.y maps to ~y*1000 px/s².
 * Used so shard fall matches Fall runway gravity feel.
 */
export var FLASK_SHARD_MATTER_GRAVITY_TO_PX = 1000;
/** Safety cap so a mis-sized viewport cannot retain bursts forever. */
export var FLASK_SHARD_BURST_MAX_MS = 15000;

/** Crack / hit anchor points (normalized within letterboxed flask body). */
export var FLASK_CRACK_ORIGINS = [
  { centerX: 0.5, centerY: 0.52 },
  { centerX: 0.43, centerY: 0.70 },
];

/** Fallback origin when crack index is unavailable (e.g. shield hit). */
export var FLASK_SHARD_DEFAULT_ORIGIN = { centerX: 0.5, centerY: 0.52 };

/**
 * @param {number} flaskHp
 * @returns {number} 0 = pristine, 1 = light damage, 2 = heavy damage
 */
export function getFlaskDamageSeverity(flaskHp) {
  if (flaskHp <= 0) return 0;
  if (flaskHp === 1) return 2;
  if (flaskHp === 2) return 1;
  return 0;
}

/**
 * @param {number} runHeightPx
 * @param {object} [runConfig]
 * @returns {number} gravity in px/s²
 */
export function getFlaskShardGravityPx(runHeightPx, runConfig) {
  return getFallGravityForRunHeight(runHeightPx, runConfig) * FLASK_SHARD_MATTER_GRAVITY_TO_PX;
}

/**
 * @param {number} seed
 * @param {number} n
 * @returns {number} 0..1
 */
export function hash01(seed, n) {
  var s = Math.sin((seed + n * 12.9898) * 0.0001 + n * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} layout
 * @param {number} crackIndex 0 = first crack, 1 = second; other values use default center
 * @param {number} startedAtMs
 * @param {{ countMin?: number, countMax?: number }} [opts]
 * @returns {{ startedAt: number, particles: Array<object> }|null}
 */
export function createFlaskShardBurst(layout, crackIndex, startedAtMs, opts) {
  if (!layout) return null;
  opts = opts || {};

  var origin = FLASK_CRACK_ORIGINS[crackIndex] || FLASK_SHARD_DEFAULT_ORIGIN;
  var originX = layout.drawX + layout.drawW * origin.centerX;
  var originY = layout.drawY + layout.drawH * origin.centerY;
  var minDim = Math.min(layout.drawW, layout.drawH);
  var seed = Math.floor(Number(startedAtMs) || 0) + (Number(crackIndex) || 0) * 9176 + 1;
  var countMin = opts.countMin != null ? Math.floor(Number(opts.countMin)) : FLASK_SHARD_BURST_COUNT_MIN;
  var countMax = opts.countMax != null ? Math.floor(Number(opts.countMax)) : FLASK_SHARD_BURST_COUNT_MAX;
  if (!isFinite(countMin) || countMin < 1) countMin = FLASK_SHARD_BURST_COUNT_MIN;
  if (!isFinite(countMax) || countMax < countMin) countMax = countMin;
  var span = countMax - countMin + 1;
  var count = countMin + Math.floor(hash01(seed, 0.17) * span);
  var sizeSpan = FLASK_SHARD_BURST_SIZE_MAX_RATIO - FLASK_SHARD_BURST_SIZE_MIN_RATIO;
  var particles = [];

  var arcMin = FLASK_SHARD_BURST_ANGLE_MIN_RAD;
  var arcMax = FLASK_SHARD_BURST_ANGLE_MAX_RAD;
  var arcSpan = arcMax - arcMin;
  for (var i = 0; i < count; i++) {
    var angleStep = arcSpan / count;
    var sectorCenter = arcMin + angleStep * i + angleStep * 0.5;
    var angleJitter = (hash01(seed, i * 6.73 + 2.8) - 0.5) * angleStep * 0.35;
    var outA = sectorCenter + angleJitter;
    if (outA < arcMin) outA = arcMin;
    if (outA > arcMax) outA = arcMax;
    var dirX = Math.cos(outA);
    var dirY = -Math.sin(outA); // math up → screen up
    var clusterR = minDim * (0.008 + hash01(seed, i * 2.11 + 0.4) * 0.018);
    var spawnX = originX + dirX * clusterR;
    var spawnY = originY + dirY * clusterR;
    var speed = minDim * (0.22 + hash01(seed, i * 8.19 + 3.5) * 0.42) * FLASK_SHARD_BURST_SPEED_SCALE;
    particles.push({
      x: spawnX,
      y: spawnY,
      vx: dirX * speed,
      vy: dirY * speed,
      size: minDim * (FLASK_SHARD_BURST_SIZE_MIN_RATIO + hash01(seed, i * 9.61 + 5.2) * sizeSpan),
      rotationRad: hash01(seed, i * 11.37 + 6.2) * Math.PI * 2,
      rotationSpeed: (hash01(seed, i * 13.71 + 7.4) - 0.5) * 8,
      variant: hash01(seed, i * 15.83 + 8.6) < 0.5 ? 0 : 1,
    });
  }

  return {
    startedAt: Math.floor(Number(startedAtMs) || 0),
    particles: particles,
  };
}

/**
 * Ballistic position for a shard particle (px/s velocity, px/s² gravity).
 *
 * @param {{ x: number, y: number, vx: number, vy: number, rotationRad?: number, rotationSpeed?: number, size?: number }} p
 * @param {number} startedAtMs
 * @param {number} nowMs
 * @param {number} gravityPx
 * @returns {{ x: number, y: number, rotation: number }}
 */
export function shardParticlePose(p, startedAtMs, nowMs, gravityPx) {
  var t = (Math.floor(Number(nowMs) || 0) - Math.floor(Number(startedAtMs) || 0)) / 1000;
  if (t < 0) t = 0;
  var g = Number(gravityPx);
  if (!isFinite(g) || g < 0) g = 0;
  return {
    x: p.x + p.vx * t,
    y: p.y + p.vy * t + 0.5 * g * t * t,
    rotation: (p.rotationRad || 0) + (p.rotationSpeed || 0) * t,
  };
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} halfSize
 * @param {{ left: number, top: number, right: number, bottom: number }} viewport
 * @returns {boolean}
 */
export function isShardParticleOffscreen(x, y, halfSize, viewport) {
  if (!viewport) return false;
  var pad = halfSize > 0 ? halfSize : 0;
  return (
    x + pad < viewport.left
    || x - pad > viewport.right
    || y + pad < viewport.top
    || y - pad > viewport.bottom
  );
}

/**
 * Keep bursts that still have at least one on-screen particle.
 *
 * @param {Array<{ startedAt: number, particles: Array<object> }>} bursts
 * @param {number} nowMs
 * @param {{ left: number, top: number, right: number, bottom: number }|null} [viewport]
 * @param {number} [gravityPx]
 * @returns {Array<{ startedAt: number, particles: Array<object> }>}
 */
export function pruneFlaskShardBursts(bursts, nowMs, viewport, gravityPx) {
  if (!bursts || !bursts.length) return [];
  var now = Math.floor(Number(nowMs) || 0);
  var g = Number(gravityPx);
  if (!isFinite(g) || g < 0) g = 0;
  var kept = [];

  for (var i = 0; i < bursts.length; i++) {
    var burst = bursts[i];
    if (now - burst.startedAt >= FLASK_SHARD_BURST_MAX_MS) continue;
    var particles = burst.particles || [];
    if (!particles.length) continue;

    if (!viewport) {
      kept.push(burst);
      continue;
    }

    var alive = [];
    for (var p = 0; p < particles.length; p++) {
      var particle = particles[p];
      var pose = shardParticlePose(particle, burst.startedAt, now, g);
      var half = (particle.size || 0) / 2;
      if (!isShardParticleOffscreen(pose.x, pose.y, half, viewport)) {
        alive.push(particle);
      }
    }
    if (alive.length > 0) {
      if (alive.length !== particles.length) {
        kept.push({ startedAt: burst.startedAt, particles: alive });
      } else {
        kept.push(burst);
      }
    }
  }
  return kept;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{ startedAt: number, particles: Array<object> }>} bursts
 * @param {number} nowMs
 * @param {(variant: 0|1) => CanvasImageSource|null} getShardImage
 * @param {{ gravityPx?: number, viewport?: { left: number, top: number, right: number, bottom: number }, playfield?: { sprite: function }, worldOrigin?: { x?: number, y?: number } }} [drawOpts]
 * @returns {boolean}
 */
export function drawFlaskShardBursts(ctx, bursts, nowMs, getShardImage, drawOpts) {
  if (!ctx || !bursts || !bursts.length || typeof getShardImage !== "function") return false;

  drawOpts = drawOpts || {};
  var now = Math.floor(Number(nowMs) || 0);
  var gravityPx = Number(drawOpts.gravityPx);
  if (!isFinite(gravityPx) || gravityPx < 0) gravityPx = 0;
  var viewport = drawOpts.viewport || null;
  var drewAny = false;

  for (var b = 0; b < bursts.length; b++) {
    var burst = bursts[b];
    var elapsedMs = now - burst.startedAt;
    if (elapsedMs < 0 || elapsedMs >= FLASK_SHARD_BURST_MAX_MS) continue;
    var particles = burst.particles || [];

    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      var img = getShardImage(p.variant);
      if (!img || !img.naturalWidth || !img.naturalHeight) continue;

      var pose = shardParticlePose(p, burst.startedAt, now, gravityPx);
      var half = (p.size || 0) / 2;
      if (viewport && isShardParticleOffscreen(pose.x, pose.y, half, viewport)) continue;

      var playfield = drawOpts.playfield;
      if (playfield && typeof playfield.sprite === "function") {
        var origin = drawOpts.worldOrigin || { x: 0, y: 0 };
        var parentAlpha = ctx && ctx.globalAlpha != null && isFinite(ctx.globalAlpha) ? ctx.globalAlpha : 1;
        playfield.sprite({
          key: "shard-" + burst.startedAt + "-" + b + "-" + i,
          image: img,
          x: (origin.x || 0) + pose.x,
          y: (origin.y || 0) + pose.y,
          width: p.size,
          height: p.size,
          rotation: pose.rotation,
          alpha: parentAlpha * FLASK_SHARD_BURST_OPACITY,
          zIndex: 16,
        });
        drewAny = true;
        continue;
      }

      ctx.save();
      var parentAlpha = ctx.globalAlpha != null && isFinite(ctx.globalAlpha) ? ctx.globalAlpha : 1;
      ctx.globalAlpha = parentAlpha * FLASK_SHARD_BURST_OPACITY;
      ctx.imageSmoothingEnabled = true;
      if (ctx.imageSmoothingQuality != null) ctx.imageSmoothingQuality = "high";
      ctx.translate(pose.x, pose.y);
      ctx.rotate(pose.rotation);
      ctx.drawImage(
        img,
        0,
        0,
        img.naturalWidth,
        img.naturalHeight,
        -p.size / 2,
        -p.size / 2,
        p.size,
        p.size
      );
      ctx.restore();
      drewAny = true;
    }
  }

  return drewAny;
}
